
































// Vue
import Vue from "vue";
import { mapGetters } from "vuex";
import store from "@/core/services/store";
// DayPilot Scheduler
import { DayPilot } from "daypilot-pro-vue";
import Scheduler from "@/views/dispatch/jobs/scheduler/Scheduler.vue";
import UpdateJobDialog from "./job-dashboard/_dialogs/UpdateJobDialog.vue";
// moment.js
import moment from "moment";
// PNotify
import { error } from "@pnotify/core";
import "@pnotify/core/dist/PNotify.css";
import "@pnotify/core/dist/BrightTheme.css";
import "@pnotify/confirm/dist/PNotifyConfirm.css";
import * as Confirm from "@pnotify/confirm";
// Models
import { ScheduleEvent } from "./scheduler/_models/scheduleEvent.model";
import { ScheduleResource } from "./scheduler/_models/scheduleResource.model";
import { JobSchedulerSelection } from "./scheduler/_models/jobSchedulerSelection.model";
import {
  JobModel,
  JobApplicationModel,
  DriverModel,
  UpdateDeliveryDetailsModel,
  MoveJobModel
} from "@/core/api/apiClient";
// Services
import JobsService from "./Jobs.service";
// enum mappings
import mapPriceType from "@/core/helpers/enum_mappings/PriceType";
import mapQuantityType from "@/core/helpers/enum_mappings/QuantityType";

export default Vue.extend({
  name: "JobScheduler",
  components: { Scheduler, UpdateJobDialog },
  data() {
    return {
      isLoading: false,

      lastUsedDate: new Date().toISOString() as string,
      jobsData: [] as JobModel[],

      //#region Schedule Jobs
      selectedDriverDateRange: undefined as JobSchedulerSelection | undefined,
      drivers: null as DriverModel[] | null,
      showJobDialog: false,
      selectedJob: undefined as JobModel | undefined,
      //#endregion Schedule Jobs

      // * DayPilot config
      config: {
        clientId: "",
        events: [] as ScheduleEvent[],
        resources: [] as ScheduleResource[],

        cellWidthSpec: "Fixed",
        cellWidth: 70,
        eventHeight: 70,
        // rowMinHeight: 60,

        timeHeaders: [
          { groupBy: "Day", format: "dddd, d MMMM yyyy" },
          { groupBy: "Hour" },
          { groupBy: "Cell", format: "mm" }
        ],
        scale: "CellDuration",
        cellDuration: 30,
        days: 1,
        businessWeekends: true,
        businessBeginsHour: 0,
        businessEndsHour: 24,

        startDate: DayPilot.Date.today(),

        showNonBusiness: true,
        timeRangeSelectedHandling: "Enabled",
        timeRangeSelectingStartEndEnabled: true,
        timeRangeSelectingStartEndFormat: "h:mm tt",
        onTimeRangeSelect: async (args) => {
          const { start, end, resource } = args;
          const self = this as any;

          // Populate drivers on first time range selection
          self.drivers ??= await JobsService.getAllDrivers(self.currentUser.clientId);

          const driver = (self.drivers as DriverModel[]).find((x) => x.driverId === resource);

          self.selectedDriverDateRange = {
            startDate: start.value,
            endDate: end.value,
            driver: driver
          } as JobSchedulerSelection;

          self.showJobDialog = true;
        },

        /**
         * Event handler for when the user drags and drops an event on the scheduler.
         */
        eventMoveHandling: "Update",
        onEventMoved: (args: any) => {
          // Event metadata
          const eventData = args.e.data;
          const previousResource = eventData.previousResource;
          const resource = eventData.resource;
          const clientId = eventData.clientId;
          const job = eventData.job as JobModel;

          // Build deliveryDate and endDate
          const deliveryDate = moment(eventData.start.value).toDate();
          const estimateEndDate = moment(eventData.end.value).toDate();

          // Check if the target driver is already assigned at any time
          // (except when rescheduling the same driver)
          const isAssignedToOtherDriver = job.drivers.some(
            (d) => d.driverId === resource && resource !== previousResource
          );

          if (isAssignedToOtherDriver) {
            // Trying to assign to a driver who already has this job — block
            return;
          }

          // Skip if same driver and same time
          const isSameDriver = previousResource === resource;
          const isSameTime =
            moment(deliveryDate).isSame(job.deliveryDetails!.deliveryDate) &&
            moment(estimateEndDate).isSame(job.deliveryDetails!.estimateEndDate);

          if (isSameDriver && isSameTime) {
            return;
          }

          const payload: MoveJobModel = new MoveJobModel({
            previousDriverId: previousResource,
            newDriverId: resource,
            deliveryDate: deliveryDate,
            estimateEndDate: estimateEndDate
          });

          //update job delivery details
          JobsService.moveJobSchedule(clientId, job.jobId, payload)
            .then(() => {
              // Refresh activeJobs
              JobsService.getAllActiveJobs(clientId).then(() => {
                store.dispatch("SET_UPDATE_DASHBOARD", true);
              });
            })
            .catch((e: any) => {
              // Toast error
              error({
                title: "Error",
                text: `There was an error updating the Job: ${e}`,
                hide: true,
                closer: false,
                sticker: false,
                modules: new Map([
                  [
                    Confirm,
                    {
                      confirm: true,
                      buttons: [
                        {
                          text: "Ok",
                          primary: true,
                          click: (notice: any) => notice.close()
                        }
                      ]
                    }
                  ]
                ])
              });
            });
        },

        /**
         * Event handler for when a user resizes an event on the schedule.
         */
        eventResizeHandling: "Update",
        onEventResized: (args: any) => {
          // Event metadata
          const eventData = args.e.data;
          const clientId = eventData.clientId;
          const job = eventData.job as JobModel;

          // Build deliveryDate and endDate
          const deliveryDate = moment(eventData.start.value).toDate();
          const estimateEndDate = moment(eventData.end.value).toDate();

          //update job delivery details
          const payload: UpdateDeliveryDetailsModel = new UpdateDeliveryDetailsModel({
            deliveryDate: deliveryDate,
            estimateEndDate: estimateEndDate,
            deliveryDateType: job.deliveryDetails!.deliveryDateType,
            isTaxable: job.deliveryDetails!.isTaxable,
            priceType: job.deliveryDetails!.priceType,
            price: job.deliveryDetails!.price,
            notes: job.deliveryDetails!.notes
          });

          //update job delivery details
          JobsService.updateJobDeliveryDetails(clientId, job.jobId, payload)
            .then(() => {
              // Refresh activeJobs
              JobsService.getAllActiveJobs(clientId).then(() => {
                store.dispatch("SET_UPDATE_DASHBOARD", true);
              });
            })
            .catch((e: any) => {
              // Toast error
              error({
                title: "Error",
                text: `There was an error updating the Job: ${e}`,
                hide: true,
                closer: false,
                sticker: false,
                modules: new Map([
                  [
                    Confirm,
                    {
                      confirm: true,
                      buttons: [
                        {
                          text: "Ok",
                          primary: true,
                          click: (notice: any) => notice.close()
                        }
                      ]
                    }
                  ]
                ])
              });
            });
        },

        eventDeleteHandling: "Disabled",

        eventClickHandling: "Enabled",
        onEventClick: async (args: any) => {
          // Event metadata
          const eventData = args.e.data;

          const self = this as any;

          // Populate drivers on first time range selection
          self.drivers ??= await JobsService.getAllDrivers(self.currentUser.clientId);

          self.selectedJob = eventData.job as JobModel;
          self.showJobDialog = true;
        },

        eventHoverHandling: "Bubble"
      }
    };
  },

  mounted() {
    // Pass clientId to DayPilot config
    this.config.clientId = this.currentUser.clientId;
    this.getJobs(new Date().toISOString());
  },

  methods: {
    getJobs(date: string): void {
      this.isLoading = true;
      this.lastUsedDate = date;
      const start = new Date(new Date(date).setHours(0, 0, 0, 0));
      const end = new Date(new Date(date).setHours(23, 59, 59));

      JobsService.getJobsDateRange(this.currentUser.clientId, start, end, true, true, false)
        .then((data: JobModel[]) => {
          if (!data) return;
          this.jobsData = data;
          this.getEvents();
          this.getResources();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    /**
     * Generates ScheduleEvents for the DayPilot Scheduler, using Jobs from state
     * @author Nick Brahimir
     */
    getEvents(): void {
      const scheduleEvents: ScheduleEvent[] = [];
      this.jobsData.forEach((element: JobModel) => {
        // ! Add utcoffset hours here
        // ? x1000 converts the UNIX timestamp
        const momentStartDate = moment(element.deliveryDetails?.deliveryDate);
        const utcOffset = Math.abs(momentStartDate.utcOffset() / 60);
        const start = moment(element.deliveryDetails?.deliveryDate)
          .subtract(utcOffset, "hours")
          .toISOString();

        let end: string;

        // Default EstimateEndDate
        end = moment(element.deliveryDetails?.deliveryDate)
          .add(1, "hours")
          .subtract(utcOffset, "hours")
          .toISOString();

        // If the Job HAS an EstimateEndDate, set the ScheduleEvent "end" here.
        if (element.deliveryDetails?.estimateEndDate) {
          end = moment(element.deliveryDetails?.estimateEndDate)
            .subtract(utcOffset, "hours")
            .toISOString();
        }

        if (!end || new Date(end) <= new Date(start)) {
          // Set end to 1 hour after start if invalid
          end = new Date(new Date(start).getTime() + 60 * 60 * 1000).toISOString();
        }
        if (element.drivers?.length) {
          element.drivers.forEach((driver) => {
            const index = element.drivers?.indexOf(driver);
            // Build the Event html
            const html = this.buildEventHtml(element);

            // Build the on-hover Event bubbleHtml
            const bubbleHtml = this.buildEventBubbleHtml(element);

            let barColor;
            let backColor;

            // Check if Job is Completed or isOverdueJob() and color Event bars accordingly
            if (element.completed) {
              barColor = "#008000";
              backColor = "#acf8ac";
            }
            // ! Add utcoffset hours here for the comparison.
            else if (this.isOverdueJob(moment(start).add(utcOffset, "hours").toISOString())) {
              barColor = "#a90000";
              backColor = "#ffd1d1";
            } else {
              barColor = "#1167a8";
              backColor = "#f6f6f6";
            }

            // Build the Schedule Event object
            let event: ScheduleEvent = {
              id: element.jobId! + index,
              start: start,
              end: end,
              previousResource: driver.driverId ?? "",
              resource: driver.driverId ?? "",
              html: html,
              job: element,
              clientId: this.currentUser.clientId,
              bubbleHtml: bubbleHtml,

              barColor: barColor,
              backColor: backColor
            };
            scheduleEvents.push(event);
          });
        }
      });

      this.config.events = scheduleEvents;
    },

    /**
     * Generates ScheduleResources for the DayPilot Scheduler, by fetching Drivers
     * @author Nick Brahimir
     */
    getResources(): void {
      JobsService.getAllDrivers(this.currentUser.clientId).then((data: DriverModel[]) => {
        if (!data) return;

        // Resize Scheduler cells if there are few Drivers.
        if (data.length <= 3) {
          this.config.cellWidth = 100;
          this.config.eventHeight = 100;
        }

        const scheduleResources: ScheduleResource[] = [];

        data.forEach((driver: DriverModel) => {
          scheduleResources.push({
            id: driver.driverId!,
            name: `${driver.firstName} ${driver.lastName}`
          });
        });

        this.config.resources = scheduleResources;
      });
    },

    buildEventHtml(element: JobModel): string {
      let eventHtml = `<div>`;

      // Add Customer Name
      if (element.customer?.name) {
        eventHtml += `<b>${element.customer.name}</b> <br/ >`;
      }

      // Add Jobsite Name/Address
      if (element.jobSite) {
        // If Jobsite has a Name, show it
        if (element.jobSite.name) eventHtml += `${element.jobSite.name} <br/ >`;

        // If Jobsite does NOT have a name, show it's Address if it has one
        if (!element.jobSite.name && element.jobSite.streetAddress)
          eventHtml += `${element.jobSite.streetAddress} <br/ >`;
      }

      // Add Job event metadata to display
      if (element.material)
        if (element.material?.material?.name) eventHtml += `${element.material.material.name} <br/ >`;
      if (element.quantity && element.quantityType)
        if (element.quantity.quantity?.name)
          eventHtml += `${element.quantity.quantity.name} ${mapQuantityType(
            element.quantityType
          )} <br/ >`;

      eventHtml += `</div>`;

      return eventHtml;
    },

    buildEventBubbleHtml(element: JobModel): string {
      let eventStatus;
      if (element.completed) {
        eventStatus = `<b style='color: green'>Complete</b>`;
      } else {
        eventStatus = `<b style='color: red'>Incomplete</b>`;
      }

      let bubbleHtml = `
        <h6>${element.customer?.name}</h6>
        ${eventStatus}
      `;

      if (element.material || element.applications.length || element.quantity) {
        bubbleHtml += `<hr class='my-2' />`;

        if (element.material)
          if (element.material.material?.name)
            bubbleHtml += `<b>Material:</b> ${element.material.material.name} <br />`;

        if (element.quantity && element.quantityType)
          if (element.quantity.quantity?.name)
            bubbleHtml += `<b>Quantity:</b> ${element.quantity.quantity.name} ${mapQuantityType(
              element.quantityType
            )} <br />`;

        if (element.applications.length) {
          let applications = "";
          element.applications.forEach((application: JobApplicationModel) => {
            const index = element.applications.indexOf(application);

            applications += application.application?.name;
            if (index !== element.applications.length - 1) applications += ", ";
          });

          bubbleHtml += `<b>Applications:</b> ${applications} <br />`;
        }
      }

      if (element.jobSiteDetails) {
        bubbleHtml += `<hr class='my-2' />`;
        if (element.jobSiteDetails.lot) bubbleHtml += `<b>Lot:</b> ${element.jobSiteDetails.lot} <br />`;

        if (element.jobSiteDetails.block)
          bubbleHtml += `<b>Block:</b> ${element.jobSiteDetails.block} <br />`;

        if (element.jobSiteDetails.unit)
          bubbleHtml += `<b>Unit:</b> ${element.jobSiteDetails.unit} <br />`;

        if (element.jobSiteDetails.municipal)
          bubbleHtml += `<b>Municipal:</b> ${element.jobSiteDetails.municipal} <br />`;

        if (element.jobSiteDetails.po) bubbleHtml += `<b>PO:</b> ${element.jobSiteDetails.po} <br />`;
      }

      if (element.deliveryDetails) {
        bubbleHtml += `<hr class='my-2' />`;
        if (element.deliveryDetails.price)
          bubbleHtml += `<b>Price:</b> $${element.deliveryDetails.price} <br />`;

        if (element.deliveryDetails.priceType)
          bubbleHtml += `<b>Price Type:</b> ${mapPriceType(element.deliveryDetails.priceType)} <br />`;
      }
      return bubbleHtml;
    },

    isOverdueJob(date: any): boolean {
      const testDate = moment(new Date(date));
      const currentDate = moment(new Date());
      return testDate < currentDate;
    },

    handleJobDialogClose() {
      this.selectedDriverDateRange = undefined;
      this.selectedJob = undefined;
      this.showJobDialog = false;
      (this.$refs.scheduler as any).clearSelection();
    }
  },

  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      activeJobs: "activeJobs"
    }),
    getCurrentOffset() {
      return Math.abs(moment().utcOffset() / 60);
    }
  },

  watch: {
    activeJobs(): void {
      this.getJobs(this.lastUsedDate);
    }
  }
});
